import * as m from 'motion/react-m'

import {
  Badge,
  Button,
  ChannelAvatar,
  Checkbox,
  CircleCheckBigIcon,
  Dialog,
  SimpleSpinner,
  Text,
} from '@buffer-mono/popcorn'

import { MonthlyCleanupSuccess } from './MonthlyCleanupSuccess'
import { useMonthlyCleanupDialog } from './useMonthlyCleanupDialog'
import type { MonthlyCleanupChannel } from './useMonthlyCleanupDialog'

import styles from './MonthlyCleanupDialog.module.css'

import type { JSX } from 'react'

type MonthlyCleanupDialogProps = {
  channels: MonthlyCleanupChannel[]
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Called once resolution finishes with at least one channel resolved. */
  onResolved?: () => void
}

/**
 * The multi-channel cleanup dialog: pick channels, resolve their comments older
 * than one month (a check appears per channel as it completes), then confirm
 * the total. Shown when more than one channel has old comments.
 */
export const MonthlyCleanupDialog = ({
  channels,
  open,
  onOpenChange,
  onResolved,
}: MonthlyCleanupDialogProps): JSX.Element => {
  const {
    channels: rows,
    isSelected,
    toggle,
    resolving,
    isResolved,
    done,
    resolvedTotal,
    selectedCount,
    selectedTotal,
    resolve,
  } = useMonthlyCleanupDialog({ channels, open, onResolved })

  const suffix = selectedTotal === 1 ? '' : 's'
  const resolveLabel = resolving
    ? 'Resolving comments…'
    : selectedCount === 0
      ? 'Select a channel'
      : `Resolve ${selectedTotal} comment${suffix}`

  // Block closing (backdrop / escape) while a resolve is in flight.
  const handleOpenChange = (next: boolean): void => {
    if (resolving) return
    onOpenChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content size="medium">
        {done ? (
          <MonthlyCleanupSuccess
            total={resolvedTotal}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <>
            <Dialog.Header>
              <Dialog.Title>Resolve comments older than one month</Dialog.Title>
              <Dialog.Description>
                Choose which channels to resolve comments from. You&apos;ll be
                able to access them using the &apos;status&apos; filter.
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Body className={styles.body}>
              <Text size="sm" color="subtle" className={styles.subheading}>
                These channels have comments older than one month
              </Text>
              <ul className={styles.list}>
                {rows.map((channel) => {
                  const resolved = isResolved(channel.id)
                  const displayCount =
                    channel.count >= channel.limit
                      ? `${channel.limit}+`
                      : `${channel.count}`

                  return (
                    <m.li
                      key={channel.id}
                      className={styles.row}
                      // Grey the whole row out as it resolves, so the list reads
                      // as a top-to-bottom progress sweep.
                      animate={{ opacity: resolved ? 0.5 : 1 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                    >
                      <Checkbox
                        className={styles.rowCheckbox}
                        checked={isSelected(channel.id)}
                        disabled={resolving}
                        onChange={() => toggle(channel.id)}
                      >
                        <span className={styles.rowContent}>
                          <ChannelAvatar
                            size="small"
                            alt=""
                            src={channel.avatar}
                            name={channel.name}
                            channel={channel.service}
                          />
                          <span className={styles.name}>{channel.name}</span>
                          <Badge size="medium" variant="neutral">
                            {displayCount}
                          </Badge>
                          <span
                            className={styles.status}
                            aria-hidden={!resolved}
                          >
                            {resolved && (
                              <m.span
                                className={styles.check}
                                initial={{ opacity: 0, scale: 0.6 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.2, ease: 'easeOut' }}
                              >
                                <CircleCheckBigIcon size="small" />
                              </m.span>
                            )}
                          </span>
                        </span>
                      </Checkbox>
                    </m.li>
                  )
                })}
              </ul>
            </Dialog.Body>
            <Dialog.Footer>
              <Button
                variant="tertiary"
                disabled={resolving}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={resolving || selectedCount === 0}
                onClick={() => void resolve()}
              >
                {resolving && <SimpleSpinner size="small" />}
                {resolveLabel}
              </Button>
            </Dialog.Footer>
          </>
        )}
      </Dialog.Content>
    </Dialog>
  )
}
